import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES } from "../../data/menu";

const line = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.15, delay: 0.25 + i * 0.12, ease: [0.19, 1, 0.22, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ink"
    >
      {/* Parallax hero image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 z-0"
        aria-hidden
      >
        <img
          src={IMAGES.hero}
          alt=""
          className="w-full h-[115%] object-cover object-center"
          draggable={false}
        />
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ink" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/30 to-ink" />
      </motion.div>

      {/* Corner marks */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute top-28 md:top-32 left-6 md:left-12 z-10 text-[11px] uppercase tracking-widest-2 text-bone/60"
        data-testid="hero-eyebrow-left"
      >
        <div className="w-8 h-px bg-bone/40 mb-3" />
        Est. MMXIX · Est. 2019
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute top-28 md:top-32 right-6 md:right-12 z-10 text-right text-[11px] uppercase tracking-widest-2 text-bone/60"
        data-testid="hero-eyebrow-right"
      >
        <div className="w-8 h-px bg-bone/40 mb-3 ml-auto" />
        Vol. 07 · The Winter Menu
      </motion.div>

      {/* Kinetic type block */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-[1600px] w-full mx-auto">
          <motion.div
            initial="hidden"
            animate="show"
            className="font-serif font-light text-bone leading-[0.82] tracking-tightest"
          >
            <span className="mask-line">
              <motion.span variants={line} custom={0} className="block text-[15vw] md:text-[13vw]">
                The Art
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} custom={1} className="block text-[15vw] md:text-[13vw] italic pl-[8vw]">
                of Slow
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} custom={2} className="block text-[15vw] md:text-[13vw]">
                Extraction<span className="text-gold">.</span>
              </motion.span>
            </span>
          </motion.div>

          <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 md:gap-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 1 }}
              className="col-span-12 md:col-span-5 md:col-start-2 text-[13px] md:text-sm text-bone/70 leading-relaxed font-light max-w-md"
              data-testid="hero-description"
            >
              A specialty coffee bar in the old iron quarter — where beans travel
              further than most people do, and every cup is pulled with the patience
              of a slow winter.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.55, duration: 1 }}
              className="col-span-12 md:col-span-4 md:col-start-9 flex md:justify-end items-end gap-6"
            >
              <div className="text-[11px] uppercase tracking-widest-2 text-bone/50 leading-relaxed">
                <div className="text-bone/80 mb-1">Scroll</div>
                <div className="text-bone/40">— the winter menu below</div>
              </div>
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                className="h-16 w-px bg-gradient-to-b from-bone/60 to-transparent"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
